import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Upload, Trash2, ArrowLeft, Eye, Loader2, Video } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FEATURES as LANDING_FEATURES } from './LandingPage';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

// Source unique : on dérive automatiquement la liste éditable depuis LandingPage.
// Conséquence : ajouter une nouvelle feature dans LandingPage la rend immédiatement
// éditable côté admin, sans la moindre intervention manuelle ici.
const FEATURES_META = LANDING_FEATURES.map(f => ({ slug: f.slug, title: f.title }));

export const AdminLandingPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    hero_title_part1: '',
    hero_highlight: '',
    hero_title_part2: '',
    hero_subtitle: '',
    story_paragraphs: [],
    story_quote: '',
    pricing_intro: '',
    features: {},
  });
  const [activeTab, setActiveTab] = useState('hero');

  useEffect(() => {
    axios.get(`${API}/public/landing-config`, { withCredentials: true })
      .then(r => {
        const d = r.data || {};
        setConfig(prev => ({ ...prev, ...d, features: d.features || {} }));
      })
      .catch(() => null)
      .finally(() => setLoading(false));
  }, []);

  const setField = (field, value) => setConfig(c => ({ ...c, [field]: value }));

  const setFeatureField = (slug, field, value) => {
    setConfig(c => ({
      ...c,
      features: {
        ...c.features,
        [slug]: { ...(c.features[slug] || {}), [field]: value },
      },
    }));
  };

  const setBullet = (slug, idx, value) => {
    const current = (config.features[slug]?.bullets) || [];
    const next = [...current];
    next[idx] = value;
    setFeatureField(slug, 'bullets', next);
  };

  const addBullet = (slug) => {
    const current = (config.features[slug]?.bullets) || [];
    setFeatureField(slug, 'bullets', [...current, '']);
  };

  const removeBullet = (slug, idx) => {
    const current = (config.features[slug]?.bullets) || [];
    setFeatureField(slug, 'bullets', current.filter((_, i) => i !== idx));
  };

  const setStoryParagraph = (idx, value) => {
    const next = [...(config.story_paragraphs || [])];
    next[idx] = value;
    setField('story_paragraphs', next);
  };

  const addStoryParagraph = () => {
    setField('story_paragraphs', [...(config.story_paragraphs || []), '']);
  };

  const removeStoryParagraph = (idx) => {
    setField('story_paragraphs', (config.story_paragraphs || []).filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Tous les champs sont envoyés tels quels (y compris vides).
      // Le backend interprète "vide" comme $unset (retour au default).
      const payload = { ...config };
      // Pour les paragraphes story : envoyer la liste nettoyée (chaîne vide = supprimé du tableau)
      if (Array.isArray(payload.story_paragraphs)) {
        payload.story_paragraphs = payload.story_paragraphs
          .map(p => (p || '').trim())
          .filter(p => p.length > 0);
      }
      await axios.put(`${API}/admin/landing-config`, payload, { withCredentials: true });
      toast.success('Landing page mise à jour');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 pb-32" data-testid="admin-landing-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-zinc-950/95 backdrop-blur-md border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/app" className="text-zinc-400 hover:text-white" data-testid="admin-landing-back">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-bold text-lg">CMS Landing Page</h1>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-200 text-sm border border-zinc-800"
              data-testid="admin-landing-preview"
            >
              <Eye className="w-4 h-4" /> Voir la landing
            </a>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold text-sm transition-colors disabled:opacity-60"
              data-testid="admin-landing-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-6 flex items-center gap-1 overflow-x-auto">
          {[
            { id: 'hero', label: 'Hero' },
            { id: 'story', label: 'Mon histoire' },
            { id: 'pricing', label: 'Tarifs' },
            { id: 'features', label: 'Fonctionnalités (14 onglets)' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === t.id ? 'text-amber-400 border-amber-400' : 'text-zinc-500 border-transparent hover:text-zinc-300'
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {activeTab === 'hero' && (
          <HeroEditor config={config} setField={setField} />
        )}
        {activeTab === 'story' && (
          <StoryEditor
            config={config}
            setField={setField}
            setStoryParagraph={setStoryParagraph}
            addStoryParagraph={addStoryParagraph}
            removeStoryParagraph={removeStoryParagraph}
          />
        )}
        {activeTab === 'pricing' && (
          <PricingEditor config={config} setField={setField} />
        )}
        {activeTab === 'features' && (
          <FeaturesEditor
            config={config}
            setFeatureField={setFeatureField}
            setBullet={setBullet}
            addBullet={addBullet}
            removeBullet={removeBullet}
            onVideoChanged={(slug, url, fileId) => {
              setFeatureField(slug, 'videoSrc', url);
              if (fileId !== undefined) setFeatureField(slug, 'video_file_id', fileId);
            }}
          />
        )}
      </main>
    </div>
  );
};

// --- Sous-composants éditeurs ---

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[11px] text-zinc-600">{hint}</p>}
  </div>
);

const TextInput = (props) => (
  <input
    {...props}
    className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/50 placeholder:text-zinc-600"
  />
);

const TextArea = (props) => (
  <textarea
    {...props}
    className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/50 placeholder:text-zinc-600 leading-relaxed"
  />
);

const HeroEditor = ({ config, setField }) => (
  <section className="space-y-6">
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
      <p className="text-xs text-zinc-400 mb-4">
        Le titre Hero s'affiche sous la forme :{' '}
        <strong className="text-zinc-200">[Partie 1] [Mot doré]. [Partie 2]</strong>.
        Laisse vide pour garder le texte par défaut.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Titre — partie 1" hint="Ex: L'app tout-en-un que les MDB">
          <TextInput
            value={config.hero_title_part1 || ''}
            onChange={e => setField('hero_title_part1', e.target.value)}
            placeholder="L'app tout-en-un que les MDB"
            data-testid="hero-title-part1"
          />
        </Field>
        <Field label="Mot mis en valeur (doré)" hint="Ex: attendaient">
          <TextInput
            value={config.hero_highlight || ''}
            onChange={e => setField('hero_highlight', e.target.value)}
            placeholder="attendaient"
            data-testid="hero-highlight"
          />
        </Field>
        <Field label="Titre — partie 2 (en gris)" hint="Ex: Faite par un MDB.">
          <TextInput
            value={config.hero_title_part2 || ''}
            onChange={e => setField('hero_title_part2', e.target.value)}
            placeholder="Faite par un MDB."
            data-testid="hero-title-part2"
          />
        </Field>
      </div>
      <div className="mt-4">
        <Field label="Sous-titre / accroche" hint="Le paragraphe sous le grand titre.">
          <TextArea
            rows={4}
            value={config.hero_subtitle || ''}
            onChange={e => setField('hero_subtitle', e.target.value)}
            placeholder="De la recherche..."
            data-testid="hero-subtitle"
          />
        </Field>
      </div>
    </div>
  </section>
);

const StoryEditor = ({ config, setField, setStoryParagraph, addStoryParagraph, removeStoryParagraph }) => {
  const paragraphs = config.story_paragraphs || [];
  return (
    <section className="space-y-6">
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
        <p className="text-xs text-zinc-400 mb-4">
          Tu peux écrire en HTML simple. Pour mettre un mot en couleur dorée :
          <code className="text-amber-400 ml-1">&lt;strong className="text-amber-400"&gt;mot&lt;/strong&gt;</code>.
          Pour du blanc : <code className="text-zinc-200 ml-1">&lt;strong className="text-white"&gt;mot&lt;/strong&gt;</code>.
        </p>
        <Field label="Paragraphes de ton histoire" hint="Si tu en ajoutes ici, ils REMPLACENT les 5 paragraphes par défaut. Laisse vide pour garder ceux par défaut.">
          <div className="space-y-3">
            {paragraphs.map((p, i) => (
              <div key={i} className="flex gap-2">
                <TextArea
                  rows={3}
                  value={p}
                  onChange={e => setStoryParagraph(i, e.target.value)}
                  placeholder={`Paragraphe ${i + 1}...`}
                  data-testid={`story-paragraph-${i}`}
                />
                <button
                  onClick={() => removeStoryParagraph(i)}
                  className="px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 self-start"
                  data-testid={`story-remove-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              onClick={addStoryParagraph}
              className="px-3 py-2 text-sm rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
              data-testid="story-add-paragraph"
            >
              + Ajouter un paragraphe
            </button>
          </div>
        </Field>
        <div className="mt-5">
          <Field label="Citation finale (en italique doré)">
            <TextArea
              rows={3}
              value={config.story_quote || ''}
              onChange={e => setField('story_quote', e.target.value)}
              placeholder="Aujourd'hui je l'utilise tous les jours..."
              data-testid="story-quote"
            />
          </Field>
        </div>
      </div>
    </section>
  );
};

const PricingEditor = ({ config, setField }) => (
  <section className="space-y-6">
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
      <Field label="Texte d'introduction des tarifs">
        <TextArea
          rows={3}
          value={config.pricing_intro || ''}
          onChange={e => setField('pricing_intro', e.target.value)}
          placeholder="Un seul plan, toutes les fonctionnalités..."
          data-testid="pricing-intro"
        />
      </Field>
      <p className="mt-4 text-xs text-zinc-500">
        Note : les prix (39€ fondateur / 49€ standard) et le nombre de places restantes sont gérés
        automatiquement par le serveur via <code className="text-amber-400">/api/public/founders-count</code>.
        Pour les modifier, il faut intervenir côté code (FOUNDER_LIMIT, founder_price_eur, standard_price_eur).
      </p>
    </div>
  </section>
);

const FeaturesEditor = ({ config, setFeatureField, setBullet, addBullet, removeBullet, onVideoChanged }) => {
  return (
    <section className="space-y-4">
      {FEATURES_META.map((meta) => (
        <FeatureCard
          key={meta.slug}
          meta={meta}
          override={config.features[meta.slug] || {}}
          setFeatureField={setFeatureField}
          setBullet={setBullet}
          addBullet={addBullet}
          removeBullet={removeBullet}
          onVideoChanged={onVideoChanged}
        />
      ))}
    </section>
  );
};

const FeatureCard = ({ meta, override, setFeatureField, setBullet, addBullet, removeBullet, onVideoChanged }) => {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const bullets = override.bullets || [];

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Vidéo trop grosse — max 25 Mo. Compresse-la (ex: ffmpeg ou freeconvert.com).');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/admin/landing-config/video/${meta.slug}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      // L'upload backend a déjà persisté la config en BDD (videoSrc + video_file_id).
      // Côté frontend, on met juste à jour l'URL avec un cache-bust pour forcer le reload du <video>.
      const urlWithBust = `${res.data.url}?t=${Date.now()}`;
      onVideoChanged(meta.slug, urlWithBust, res.data.file_id);
      toast.success(`Vidéo "${meta.title}" remplacée — visible sur la landing dans quelques secondes`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleVideoDelete = async () => {
    if (!window.confirm(`Retirer la vidéo de "${meta.title}" ?`)) return;
    try {
      await axios.delete(`${API}/admin/landing-config/video/${meta.slug}`, { withCredentials: true });
      onVideoChanged(meta.slug, null);
      toast.success('Vidéo retirée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const baseUrl = override.videoSrc;
  // baseUrl peut contenir un cache-bust ?t=, on l'enlève pour l'affichage
  const cleanUrl = baseUrl ? baseUrl.split('?')[0] : null;

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden" data-testid={`feature-card-${meta.slug}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-4 hover:bg-zinc-900/60 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-amber-500/10 text-amber-400 border border-amber-500/20">{meta.slug}</span>
          <div>
            <p className="font-semibold">{override.title || meta.title}</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {cleanUrl ? `🎬 vidéo : ${cleanUrl}` : '⏸️ pas de vidéo (placeholder affiché)'}
            </p>
          </div>
        </div>
        <span className="text-zinc-500 text-sm">{open ? 'Fermer' : 'Modifier'}</span>
      </button>
      {open && (
        <div className="border-t border-zinc-800 p-5 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Titre">
              <TextInput
                value={override.title || ''}
                onChange={e => setFeatureField(meta.slug, 'title', e.target.value)}
                placeholder={meta.title}
                data-testid={`feature-${meta.slug}-title`}
              />
            </Field>
            <Field label="Tagline (sous-titre court)">
              <TextInput
                value={override.tagline || ''}
                onChange={e => setFeatureField(meta.slug, 'tagline', e.target.value)}
                placeholder="Ex: La vraie marge nette en 30 secondes."
                data-testid={`feature-${meta.slug}-tagline`}
              />
            </Field>
          </div>
          <Field label="Description longue">
            <TextArea
              rows={5}
              value={override.text || ''}
              onChange={e => setFeatureField(meta.slug, 'text', e.target.value)}
              data-testid={`feature-${meta.slug}-text`}
            />
          </Field>
          <Field label="Points clés (bullets)" hint="Liste à puces affichée sous la description.">
            <div className="space-y-2">
              {bullets.map((b, i) => (
                <div key={i} className="flex gap-2">
                  <TextInput
                    value={b}
                    onChange={e => setBullet(meta.slug, i, e.target.value)}
                    placeholder={`Point ${i + 1}`}
                    data-testid={`feature-${meta.slug}-bullet-${i}`}
                  />
                  <button
                    onClick={() => removeBullet(meta.slug, i)}
                    className="px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => addBullet(meta.slug)}
                className="px-3 py-1.5 text-xs rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
                data-testid={`feature-${meta.slug}-add-bullet`}
              >
                + Ajouter un point
              </button>
            </div>
          </Field>
          {/* Vidéo */}
          <Field label="Vidéo de démo (mobile portrait, max 25 Mo)" hint="Format conseillé : .mp4 H.264, ~360×780, 10-15s, sans son. Compresse avec freeconvert.com avant upload si > 25 Mo.">
            <div className="space-y-3">
              {cleanUrl && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                  <video
                    key={cleanUrl}
                    src={cleanUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="rounded border border-zinc-800"
                    style={{ width: '90px', height: '195px', objectFit: 'cover' }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-400 font-mono truncate">{cleanUrl}</p>
                    <button
                      onClick={handleVideoDelete}
                      className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30"
                      data-testid={`feature-${meta.slug}-delete-video`}
                    >
                      <Trash2 className="w-3 h-3" /> Retirer
                    </button>
                  </div>
                </div>
              )}
              <div>
                <input
                  ref={inputRef}
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  onChange={handleVideoUpload}
                  disabled={uploading}
                  className="hidden"
                  data-testid={`feature-${meta.slug}-upload-input`}
                />
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 text-sm font-medium disabled:opacity-60"
                  data-testid={`feature-${meta.slug}-upload-btn`}
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : (cleanUrl ? <Video className="w-4 h-4" /> : <Upload className="w-4 h-4" />)}
                  {uploading ? 'Upload en cours...' : (cleanUrl ? 'Remplacer la vidéo' : 'Uploader une vidéo')}
                </button>
              </div>
            </div>
          </Field>
        </div>
      )}
    </div>
  );
};

export default AdminLandingPage;
